import {
  Bookmark,
  BookOpen,
  ChevronRight,
  Clapperboard,
  ExternalLink,
  Home,
  Info,
  Play,
  Plus,
  Radio,
  Search,
  Utensils,
  X
} from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { initialVideos, loadRecipes, loadVideos } from "./api";
import { categoryFilters, CHANNEL_URL, fallbackRecipes, SITE_URL } from "./data";
import { useLocalStorageState, useRelativeDate, useSpatialNavigation } from "./hooks";
import { Player } from "./Player";
import type { Recipe, Video, WatchHistory } from "./types";
import "./styles.css";

const nav = [
  { id: "home", label: "Головна", icon: Home },
  { id: "watch", label: "Дивитись", icon: Clapperboard },
  { id: "recipes", label: "Рецепти", icon: Utensils },
  { id: "inspiration", label: "Ідеї", icon: BookOpen },
  { id: "saved", label: "Моє", icon: Bookmark }
];

function formatViews(views: number) {
  return new Intl.NumberFormat("uk-UA", { notation: "compact", maximumFractionDigits: 1 }).format(views);
}

function classNames(...names: Array<string | false | undefined>) {
  return names.filter(Boolean).join(" ");
}

function heroTitle(title: string) {
  return title.replace(/\s[|:].*$/, "").replace(/КЛОПОТЕНКО.*/i, "Клопотенко");
}

function App() {
  const [videos, setVideos] = useState<Video[]>(initialVideos);
  const [recipes, setRecipes] = useState<Recipe[]>(fallbackRecipes);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("Усе");
  const [activeVideo, setActiveVideo] = useState<Video | null>(null);
  const [savedIds, setSavedIds] = useLocalStorageState<string[]>("klopotenko-tv-saved", []);
  const [history, setHistory] = useLocalStorageState<WatchHistory>("klopotenko-tv-history", {});
  const [aboutOpen, setAboutOpen] = useState(false);

  useSpatialNavigation(!activeVideo && !aboutOpen);

  useEffect(() => {
    let mounted = true;
    void loadVideos().then((items) => mounted && setVideos(items));
    void loadRecipes().then((items) => mounted && setRecipes(items));
    return () => {
      mounted = false;
    };
  }, []);

  const hero = useMemo(() => videos.find((video) => video.kind !== "short") ?? videos[0], [videos]);
  const heroDate = useRelativeDate(hero?.published ?? "");

  const filteredVideos = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return videos.filter((video) => {
      const categoryMatch = category === "Усе" || video.category === category || video.kind === category.toLowerCase();
      const searchMatch =
        !normalized ||
        `${video.title} ${video.description} ${video.category}`.toLowerCase().includes(normalized);
      return categoryMatch && searchMatch;
    });
  }, [category, query, videos]);

  const shelves = useMemo(() => {
    const episodes = videos.filter((video) => video.kind === "episode");
    const specials = videos.filter((video) => video.kind === "special");
    const shorts = videos.filter((video) => video.kind === "short");
    const watched = Object.keys(history)
      .map((id) => videos.find((video) => video.id === id))
      .filter(Boolean) as Video[];
    watched.sort((a, b) => (history[b.id]?.at ?? 0) - (history[a.id]?.at ?? 0));
    const saved = videos.filter((video) => savedIds.includes(video.id));
    return { episodes, specials, shorts, watched, saved };
  }, [history, savedIds, videos]);

  const toggleSaved = useCallback(
    (videoId: string) => {
      setSavedIds((current) => (current.includes(videoId) ? current.filter((id) => id !== videoId) : [...current, videoId]));
    },
    [setSavedIds]
  );

  const markWatched = useCallback(
    (videoId: string, pct: number) => {
      setHistory((current) => {
        return { ...current, [videoId]: { pct, at: Date.now() } };
      });
    },
    [setHistory]
  );

  useEffect(() => {
    const first = document.querySelector<HTMLElement>("[data-autofocus]");
    first?.focus();
  }, []);

  if (!hero) return null;

  return (
    <>
      <div className="appShell">
        <aside className="rail" aria-label="Навігація">
          <a className="brandRail" href="#home" data-focusable data-autofocus aria-label="Клопотенко TV">
            <img src="/brand/logo-small.svg" alt="Klopotenko" />
            <span>TV</span>
          </a>
          <nav>
            {nav.map((item) => {
              const Icon = item.icon;
              return (
                <a key={item.id} href={`#${item.id}`} data-focusable>
                  <Icon size={22} />
                  <span>{item.label}</span>
                </a>
              );
            })}
          </nav>
          <button className="railInfo" onClick={() => setAboutOpen(true)} data-focusable aria-label="Про Клопотенко TV">
            <Info size={22} />
            <span>Про нас</span>
          </button>
        </aside>

        <main>
          <section
            id="home"
            className="hero"
            style={{ backgroundImage: `url(${hero.thumbnail.replace("hq720", "maxresdefault")})` }}
          >
            <header className="topbar">
              <img src="/brand/logo.svg" alt="Klopotenko" className="topLogo" />
              <div className="topActions">
                <label className="searchBox">
                  <Search size={18} />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Пошук відео, рецепта, рубрики"
                    aria-label="Пошук"
                    data-focusable
                  />
                  {query ? (
                    <button onClick={() => setQuery("")} data-focusable aria-label="Очистити пошук">
                      <X size={16} />
                    </button>
                  ) : null}
                </label>
                <a className="liveBadge" href={CHANNEL_URL} target="_blank" rel="noreferrer" data-focusable>
                  <Radio size={16} />
                  Канал Євгена
                </a>
              </div>
            </header>

            <div className="heroGradient" />
            <div className="heroContent">
              <p className="eyebrow">Готуємо разом з Євгеном Клопотенком</p>
              <h1>{heroTitle(hero.title)}</h1>
              <p className="heroCopy">{hero.description}</p>
              <div className="heroMeta">
                <span>{hero.category}</span>
                <span>{heroDate}</span>
                <span>{formatViews(hero.views)} переглядів</span>
              </div>
              <div className="heroActions">
                <button className="primaryAction" onClick={() => setActiveVideo(hero)} data-focusable>
                  <Play size={20} fill="currentColor" />
                  Дивитися
                </button>
                <button className="secondaryAction" onClick={() => toggleSaved(hero.id)} data-focusable>
                  {savedIds.includes(hero.id) ? <Bookmark size={20} fill="currentColor" /> : <Plus size={20} />}
                  {savedIds.includes(hero.id) ? "У списку" : "Мій список"}
                </button>
              </div>
            </div>

            <div className="devicePanel" aria-label="Натхнення для вашої кухні">
              <div>
                <Utensils size={21} />
                <strong>На щодень</strong>
                <span>Знайомі продукти, нові смаки</span>
              </div>
              <div>
                <BookOpen size={21} />
                <strong>З українським смаком</strong>
                <span>Страви, які збирають за столом</span>
              </div>
              <div>
                <Play size={21} />
                <strong>Готуємо разом</strong>
                <span>Від першого кроку до подачі</span>
              </div>
            </div>
          </section>

          <section id="watch" className="contentBand firstBand">
            <div className="sectionHeader">
              <div>
                <p className="microLabel">Каталог відео</p>
                <h2>{query || category !== "Усе" ? "Результати" : "Останні випуски"}</h2>
              </div>
              <div className="chips" aria-label="Фільтри">
                {categoryFilters.map((item) => (
                  <button
                    key={item}
                    className={classNames("chip", category === item && "selected")}
                    onClick={() => setCategory(item)}
                    data-focusable
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
            <VideoRow
              videos={query || category !== "Усе" ? filteredVideos : videos.slice(0, 10)}
              savedIds={savedIds}
              history={history}
              empty="Нічого не знайшлося. Спробуйте інший запит або категорію."
              onPlay={setActiveVideo}
              onSave={toggleSaved}
            />
          </section>

          {shelves.watched.length ? (
            <ContentSection title="Ви нещодавно дивилися" label="Ще трохи натхнення">
              <VideoRow videos={shelves.watched} savedIds={savedIds} history={history} onPlay={setActiveVideo} onSave={toggleSaved} />
            </ContentSection>
          ) : null}

          <ContentSection title="Гастроспадок і великі історії" label="Для великого екрану">
            <VideoRow videos={shelves.specials} savedIds={savedIds} history={history} onPlay={setActiveVideo} onSave={toggleSaved} />
          </ContentSection>

          <ContentSection title="Короткі відео, смачні ідеї" label="Хвилинка натхнення">
            <VideoRow videos={shelves.shorts} savedIds={savedIds} history={history} onPlay={setActiveVideo} onSave={toggleSaved} portrait />
          </ContentSection>

          <section id="recipes" className="contentBand">
            <div className="sectionHeader">
              <div>
                <p className="microLabel">klopotenko.com</p>
                <h2>Рецепти з сайту</h2>
              </div>
              <a className="textLink" href={SITE_URL} target="_blank" rel="noreferrer" data-focusable>
                На сайт
                <ExternalLink size={16} />
              </a>
            </div>
            <div className="recipeGrid">
              {recipes.map((recipe) => (
                <a key={recipe.id} className="recipeCard" href={recipe.url} target="_blank" rel="noreferrer" data-focusable>
                  <img src={recipe.image || "/brand/brand-strip.png"} alt="" loading="lazy" />
                  <div>
                    <span>{recipe.category}</span>
                    <h3>{recipe.title}</h3>
                    <p>
                      <BookOpen size={15} />
                      {recipe.readingTime}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </section>

          <section id="inspiration" className="contentBand inspirationBand">
            <div className="sectionHeader">
              <div>
                <p className="microLabel">До вашого столу</p>
                <h2>Що приготуємо сьогодні?</h2>
              </div>
            </div>
            <div className="inspirationGrid">
              {[
                { category: "Сезонне", title: "Смак сезону", body: "Гарбуз, яблука та інші сезонні улюбленці у нових поєднаннях." },
                { category: "Десерти", title: "Щось до чаю", body: "Солодкий привід зібрати близьких за одним столом." },
                { category: "Кухні світу", title: "Спробуймо нове", body: "Знайомство з іншими кухнями починається на вашій." },
                { category: "Поради", title: "Маленькі секрети смаку", body: "Як запекти, приправити та довести улюблену страву до смаку." }
              ].map((idea) => (
                <a key={idea.category} href="#watch" className="inspirationCard" data-focusable onClick={() => {
                  setQuery("");
                  setCategory(idea.category);
                }}>
                  <Utensils size={24} />
                  <h3>{idea.title}</h3>
                  <p>{idea.body}</p>
                  <span className="textLink">Дивитися відео <ChevronRight size={16} /></span>
                </a>
              ))}
            </div>
          </section>

          <section id="saved" className="contentBand lastBand">
            <div className="sectionHeader">
              <div>
                <p className="microLabel">Мій список</p>
                <h2>Збережене</h2>
              </div>
            </div>
            <VideoRow
              videos={shelves.saved}
              savedIds={savedIds}
              history={history}
              empty="Поки немає збережених відео. Додайте випуск кнопкою «Мій список»."
              onPlay={setActiveVideo}
              onSave={toggleSaved}
            />
          </section>

          <footer>
            <span>Клопотенко TV</span>
            <span>Готуйте. Смакуйте. Діліться з близькими.</span>
          </footer>
        </main>
      </div>

      {aboutOpen ? <AboutDialog onClose={() => setAboutOpen(false)} /> : null}
      <Player video={activeVideo} onClose={() => setActiveVideo(null)} onWatched={markWatched} />
    </>
  );
}

type SectionProps = {
  title: string;
  label: string;
  children: ReactNode;
};

function ContentSection({ title, label, children }: SectionProps) {
  return (
    <section className="contentBand">
      <div className="sectionHeader">
        <div>
          <p className="microLabel">{label}</p>
          <h2>{title}</h2>
        </div>
      </div>
      {children}
    </section>
  );
}

type RowProps = {
  videos: Video[];
  savedIds: string[];
  history: WatchHistory;
  empty?: string;
  portrait?: boolean;
  onPlay: (video: Video) => void;
  onSave: (videoId: string) => void;
};

function VideoRow({ videos, savedIds, history, empty, portrait, onPlay, onSave }: RowProps) {
  if (!videos.length) return <div className="emptyState">{empty ?? "Немає відео для цієї полиці."}</div>;
  return (
    <div className={classNames("videoRow", portrait && "portraitRow")}>
      {videos.map((video) => (
        <article key={video.id} className={classNames("videoCard", portrait && "portraitCard")} data-focusable tabIndex={0}>
          <button className="cardHit" onClick={() => onPlay(video)} data-focusable aria-label={`Дивитися ${video.title}`}>
            <img src={video.thumbnail} alt="" loading="lazy" />
            <span className="kind">{video.kind === "short" ? "Short" : video.duration}</span>
            <span className="playPuck">
              <Play size={22} fill="currentColor" />
            </span>
            {history[video.id]?.pct ? (
              <span className="progressLine">
                <i style={{ width: `${history[video.id].pct}%` }} />
              </span>
            ) : null}
          </button>
          <div className="cardCopy">
            <span>{video.category}</span>
            <h3>{video.title}</h3>
            <p>{formatViews(video.views)} переглядів</p>
          </div>
          <button className="savePill" onClick={() => onSave(video.id)} data-focusable aria-label="Додати або прибрати зі списку">
            {savedIds.includes(video.id) ? <Bookmark size={16} fill="currentColor" /> : <Plus size={16} />}
          </button>
        </article>
      ))}
    </div>
  );
}

function AboutDialog({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" || event.key === "Backspace") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="dialogShade" role="dialog" aria-modal="true" aria-label="Про Клопотенко TV">
      <section className="aboutDialog">
        <button className="iconButton" onClick={onClose} data-focusable aria-label="Закрити">
          <X size={22} />
        </button>
        <img src="/brand/logo.svg" alt="Klopotenko" />
        <h2>Смачно бути разом</h2>
        <p>
          Клопотенко TV збирає рецепти, кулінарні поради та історії української кухні від Євгена Клопотенка.
          Тут знайдеться ідея і для звичайної вечері, і для особливого приводу.
        </p>
        <div className="aboutFacts">
          <span>Українська кухня</span>
          <span>Сезонні страви</span>
          <span>Домашні десерти</span>
          <span>Кулінарні історії</span>
        </div>
        <button className="primaryAction" onClick={onClose} data-focusable>
          До рецептів
          <ChevronRight size={18} />
        </button>
      </section>
    </div>
  );
}

export default App;
